import { useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  deleteUser,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { db, authInstance } from '../config/firebase';

const COLLECTION_NAME = 'Admins';

// Firebase config for secondary app (to create users without signing out current admin)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const useAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simple hash function for password (in production, use bcrypt on backend)
  const hashPassword = (password) => {
    let hash = 0;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(16);
  };

  // Verify admin credentials using Firebase Auth
  const verifyAdmin = async (email, password) => {
    let secondaryApp = null;

    try {
      setLoading(true);
      setError(null);

      const normalizedEmail = email.toLowerCase().trim();

      // First, check if admin exists in Firestore
      const q = query(
        collection(db, COLLECTION_NAME),
        where('email', '==', normalizedEmail)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Invalid email or password');
        return { success: false, error: 'Invalid email or password' };
      }

      const adminDoc = querySnapshot.docs[0];
      const adminData = adminDoc.data();

      // Check if admin is active
      if (adminData.status !== 'active') {
        setError('Your account has been deactivated. Please contact support.');
        return { success: false, error: 'Account deactivated' };
      }

      // Verify credentials using Firebase Auth
      // Use a secondary app to avoid affecting any current auth state
      secondaryApp = initializeApp(firebaseConfig, 'VerifyApp_' + Date.now());
      const secondaryAuth = getAuth(secondaryApp);

      try {
        await signInWithEmailAndPassword(secondaryAuth, normalizedEmail, password);
        // Sign out immediately - we just needed to verify credentials
        await signOut(secondaryAuth);
      } catch (authErr) {
        if (authErr.code === 'auth/wrong-password' ||
          authErr.code === 'auth/invalid-credential' ||
          authErr.code === 'auth/user-not-found') {
          setError('Invalid email or password');
          return { success: false, error: 'Invalid email or password' };
        }
        throw authErr;
      }

      // Update last login
      const adminRef = doc(db, COLLECTION_NAME, adminDoc.id);
      await updateDoc(adminRef, {
        lastLogin: serverTimestamp()
      });

      // Return admin data (excluding password)
      const { password: _, ...safeAdminData } = adminData;
      return {
        success: true,
        admin: {
          id: adminDoc.id,
          ...safeAdminData
        }
      };
    } catch (err) {
      console.error('Error verifying admin:', err);
      setError('An error occurred. Please try again.');
      return { success: false, error: err.message };
    } finally {
      // Clean up secondary app
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (e) {
          console.warn('Error cleaning up secondary app:', e);
        }
      }
      setLoading(false);
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email) => {
    try {
      setLoading(true);
      setError(null);

      const normalizedEmail = email.toLowerCase().trim();

      // Optionally check if admin exists and is active in Firestore
      const q = query(
        collection(db, COLLECTION_NAME),
        where('email', '==', normalizedEmail)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const adminData = querySnapshot.docs[0].data();
        if (adminData.status !== 'active') {
          return {
            success: false,
            error: 'This account has been deactivated. Please contact support.'
          };
        }
      }

      // Send reset email via Firebase Auth
      console.log('Attempting to send password reset email to:', normalizedEmail);
      await sendPasswordResetEmail(authInstance, normalizedEmail);
      console.log('Password reset email sent successfully');

      return { success: true };
    } catch (err) {
      console.error('Error sending password reset:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      // Handle specific Firebase Auth errors
      if (err.code === 'auth/invalid-email') {
        return { success: false, error: 'Please enter a valid email address' };
      }
      if (err.code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many attempts. Please try again later.' };
      }
      if (err.code === 'auth/user-not-found') {
        // For security, don't reveal if email exists or not
        return { success: true };
      }
      if (err.code === 'auth/missing-android-pkg-name' ||
        err.code === 'auth/missing-continue-uri' ||
        err.code === 'auth/missing-ios-bundle-id' ||
        err.code === 'auth/invalid-continue-uri' ||
        err.code === 'auth/unauthorized-continue-uri') {
        console.error('Firebase action URL configuration error');
        return { success: false, error: 'Email service configuration error. Please contact support.' };
      }

      return { success: false, error: 'Failed to send reset email. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // Create new admin (for initial setup or by super admin)
  // Creates in BOTH Firebase Auth AND Firestore
  const createAdmin = async (adminData) => {
    let secondaryApp = null;

    try {
      setLoading(true);
      setError(null);

      const email = adminData.email.toLowerCase().trim();

      // Check if email already exists in Firestore
      const q = query(
        collection(db, COLLECTION_NAME),
        where('email', '==', email)
      );
      const existingAdmin = await getDocs(q);

      if (!existingAdmin.empty) {
        setError('An admin with this email already exists');
        return { success: false, error: 'Email already exists' };
      }

      // === CREATE IN FIREBASE AUTH ===
      // Use secondary app to avoid signing out current admin
      secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp_' + Date.now());
      const secondaryAuth = getAuth(secondaryApp);

      let firebaseUid = null;
      try {
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          email,
          adminData.password
        );
        firebaseUid = userCredential.user.uid;
        // Sign out from secondary app
        await signOut(secondaryAuth);
      } catch (authErr) {
        // Handle Firebase Auth specific errors
        if (authErr.code === 'auth/email-already-in-use') {
          setError('This email is already registered in Firebase Authentication');
          return { success: false, error: 'Email already in use in Auth' };
        }
        if (authErr.code === 'auth/weak-password') {
          setError('Password is too weak. Use at least 6 characters.');
          return { success: false, error: 'Weak password' };
        }
        if (authErr.code === 'auth/invalid-email') {
          setError('Invalid email format');
          return { success: false, error: 'Invalid email' };
        }
        throw authErr;
      }

      // === CREATE IN FIRESTORE (existing logic) ===
      // Hash password
      const hashedPassword = hashPassword(adminData.password);

      // Build admin document with geographic scope
      const adminDoc = {
        uid: firebaseUid, // Store Firebase Auth UID for reference
        firstName: adminData.firstName,
        lastName: adminData.lastName,
        email: email,
        password: hashedPassword,
        phoneNumber: adminData.phoneNumber || '',
        role: adminData.role || 'admin',
        status: 'active',
        // Geographic scope fields
        accessLevel: adminData.accessLevel || 'division',
        province: adminData.province || 'Punjab',
        division: adminData.division || 'Multan',
        createdAt: serverTimestamp(),
        lastLogin: null
      };

      // Add district/tehsil based on access level
      if (adminData.accessLevel === 'district' || adminData.accessLevel === 'tehsil') {
        adminDoc.district = adminData.district || null;
      }
      if (adminData.accessLevel === 'tehsil') {
        adminDoc.tehsil = adminData.tehsil || null;
      }

      const docRef = await addDoc(collection(db, COLLECTION_NAME), adminDoc);

      return { success: true, id: docRef.id, uid: firebaseUid };
    } catch (err) {
      console.error('Error creating admin:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      // Clean up secondary app
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (e) {
          console.warn('Error cleaning up secondary app:', e);
        }
      }
      setLoading(false);
    }
  };

  // Delete admin from BOTH Firebase Auth AND Firestore
  const deleteAdmin = async (adminId, adminEmail, adminPassword) => {
    let secondaryApp = null;

    try {
      setLoading(true);
      setError(null);

      // === DELETE FROM FIREBASE AUTH ===
      // To delete a user from Auth, we need to sign in as them first
      secondaryApp = initializeApp(firebaseConfig, 'DeleteApp_' + Date.now());
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await signInWithEmailAndPassword(
          secondaryAuth,
          adminEmail,
          adminPassword
        );
        await deleteUser(userCredential.user);
        console.log('Deleted from Firebase Auth');
      } catch (authErr) {
        console.warn('Could not delete from Firebase Auth:', authErr.message);
        // Continue to delete from Firestore even if Auth deletion fails
      }

      // === DELETE FROM FIRESTORE ===
      await deleteDoc(doc(db, COLLECTION_NAME, adminId));

      return { success: true };
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (e) {
          console.warn('Error cleaning up secondary app:', e);
        }
      }
      setLoading(false);
    }
  };

  // Change password
  const changePassword = async (adminId, currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);

      // Get admin document
      const adminRef = doc(db, COLLECTION_NAME, adminId);
      const q = query(collection(db, COLLECTION_NAME), where('__name__', '==', adminId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Admin not found');
        return { success: false, error: 'Admin not found' };
      }

      const adminData = querySnapshot.docs[0].data();

      // Verify current password
      const hashedCurrent = hashPassword(currentPassword);
      if (adminData.password !== hashedCurrent) {
        setError('Current password is incorrect');
        return { success: false, error: 'Current password is incorrect' };
      }

      // Update with new password
      const hashedNew = hashPassword(newPassword);
      await updateDoc(adminRef, {
        password: hashedNew,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Update admin in BOTH Firebase Auth AND Firestore
  // Note: Email changes are not supported due to Firebase requiring email verification
  const updateAdmin = async (adminId, adminData, currentPassword, newPassword = null) => {
    let secondaryApp = null;

    try {
      setLoading(true);
      setError(null);

      const adminRef = doc(db, COLLECTION_NAME, adminId);

      // If password is being changed, we need to update Firebase Auth
      if (newPassword && currentPassword) {
        secondaryApp = initializeApp(firebaseConfig, 'UpdateApp_' + Date.now());
        const secondaryAuth = getAuth(secondaryApp);

        try {
          // Sign in as the admin to update
          const userCredential = await signInWithEmailAndPassword(
            secondaryAuth,
            adminData.originalEmail,
            currentPassword
          );

          // Import updatePassword
          const { updatePassword } = await import('firebase/auth');

          // Update password
          await updatePassword(userCredential.user, newPassword);

          await signOut(secondaryAuth);
        } catch (authErr) {
          console.error('Firebase Auth update error:', authErr);
          if (authErr.code === 'auth/wrong-password' || authErr.code === 'auth/invalid-credential') {
            setError('Current password is incorrect');
            return { success: false, error: 'Current password is incorrect' };
          }
          if (authErr.code === 'auth/requires-recent-login') {
            setError('Please re-authenticate to make this change');
            return { success: false, error: 'Re-authentication required' };
          }
          throw authErr;
        }
      }

      // === UPDATE IN FIRESTORE ===
      // Note: Email is not updated here as it's read-only in the UI
      const updateData = {
        firstName: adminData.firstName,
        lastName: adminData.lastName,
        phoneNumber: adminData.phoneNumber || '',
        accessLevel: adminData.accessLevel || 'division',
        status: adminData.status || 'active',
        updatedAt: serverTimestamp()
      };

      // Handle geographic scope
      if (adminData.accessLevel === 'province') {
        updateData.province = adminData.province || 'Punjab';
        updateData.division = null;
        updateData.district = null;
        updateData.tehsil = null;
      } else if (adminData.accessLevel === 'division') {
        updateData.province = adminData.province || 'Punjab';
        updateData.division = adminData.division || 'Multan';
        updateData.district = null;
        updateData.tehsil = null;
      } else if (adminData.accessLevel === 'district') {
        updateData.province = adminData.province || 'Punjab';
        updateData.division = adminData.division || 'Multan';
        updateData.district = adminData.district || null;
        updateData.tehsil = null;
      } else if (adminData.accessLevel === 'tehsil') {
        updateData.province = adminData.province || 'Punjab';
        updateData.division = adminData.division || 'Multan';
        updateData.district = adminData.district || null;
        updateData.tehsil = adminData.tehsil || null;
      }

      // Update password hash if new password provided
      if (newPassword) {
        updateData.password = hashPassword(newPassword);
      }

      await updateDoc(adminRef, updateData);

      return { success: true };
    } catch (err) {
      console.error('Error updating admin:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (e) {
          console.warn('Error cleaning up secondary app:', e);
        }
      }
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    verifyAdmin,
    createAdmin,
    updateAdmin,
    deleteAdmin,
    changePassword,
    hashPassword,
    sendPasswordReset
  };
};

export default useAdmin;
